import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "contexts/ThemeContext";
import { useDispatch } from "react-redux";
import { setSelectedUser } from "config/redux/slices/userSlice";
import { AppDispatch } from "config/redux/store";
import { getAccountsByUserId } from "services/accounts/accountService";
import { UserRoundPlus, ChevronDown, Menu } from "lucide-react";
import { useSidebar } from 'contexts/SidebarContext';
import FollowingsSettings from './followings/FollowingsSettings';
import FollowingsLogs from './followings/FollowingsLogs';
import { logger } from 'utils/logger';

interface FollowingsProps {
  selectedUser: string;
}

interface AccountItem {
  _id: string;
  username: string;
  name: string;
  profile_picture_base64?: string;
}

const Followings: React.FC<FollowingsProps> = ({ selectedUser }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = useState<string>("Log");
  const tabs: string[] = ["Log", "Settings"];
  const [accounts, setAccounts] = useState<AccountItem[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);

  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const tabActiveColor = isDark ? '#FFFFFF' : '#0F172B';
  const tabInactiveColor = isDark ? '#90A1B9' : '#62748E';
  const tabSelectorColor = isDark ? '#E2E8F0' : '#314158';
  const iconBg = isDark ? '#052E16' : '#F0FDF4';
  const iconColor = isDark ? '#4ADE80' : '#22C55E';
  const buttonBorder = isDark ? '#314158' : '#E2E8F0';
  const chevronColor = isDark ? '#CAD5E2' : '#45556C';

  const selectedAccount = accounts.find(a => a._id === selectedUser);

  const switchToSettings = () => setActiveTab("Settings");

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await getAccountsByUserId();
        const accountsList = response?.data?.accounts;
        if (Array.isArray(accountsList)) {
          setAccounts(accountsList);
          if (!selectedUser && accountsList.length > 0) {
            dispatch(setSelectedUser(accountsList[0]._id));
          }
        }
      } catch (e) { logger.error('Error fetching accounts:', e); }
    };
    fetchAccounts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!dropdownRef.current?.contains(target) && !mobileDropdownRef.current?.contains(target)) setIsDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAccountSelect = (account: AccountItem) => {
    dispatch(setSelectedUser(account._id));
    setIsDropdownOpen(false);
  };

  return (
    <div className="p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0" style={{ display: 'flex', flexDirection: 'column', gap: '8px', background: containerBg, flexGrow: 1, height: '100%' }}>
      <div className="rounded-none lg:rounded-2xl" style={{ display: 'flex', flexDirection: 'column', background: mainBg, boxShadow: mainShadow, flex: 1, overflow: 'hidden' }}>
        <div className="rounded-none lg:rounded-t-2xl" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '12px 16px 0px', gap: '8px', borderBottom: `1px solid ${headerBorder}`, flexShrink: 0 }}>
          <div className="flex items-center" style={{ gap: '8px' }}>
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div className="hidden lg:flex items-center justify-center" style={{ width: '36px', height: '36px', background: iconBg, borderRadius: '12px', flexShrink: 0 }}>
              <UserRoundPlus size={24} color={iconColor} />
            </div>
            <span style={{ fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, flex: 1 }}>
              {t('actions.followings.title', 'Followings')}
            </span>
            <div ref={dropdownRef} className="hidden sm:block" style={{ position: 'relative' }}>
              <button className="flex items-center transition-opacity hover:opacity-80" style={{ padding: '8px 12px', height: '36px', width: '160px', background: mainBg, border: `1px solid ${buttonBorder}`, borderRadius: '8px', cursor: accounts.length > 1 ? 'pointer' : 'default', gap: '4px' }} onClick={() => accounts.length > 1 && setIsDropdownOpen(!isDropdownOpen)}>
                {selectedAccount && (
                  <>
                    <img src={selectedAccount.profile_picture_base64 || '/default-avatar.png'} alt={selectedAccount.name} style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }} />
                    <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{selectedAccount.username}</span>
                  </>
                )}
                {accounts.length > 1 && <ChevronDown size={20} strokeWidth={3} color={chevronColor} style={{ flexShrink: 0 }} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />}
              </button>
              {isDropdownOpen && accounts.length > 1 && (
                <div style={{ position: 'absolute', right: 0, top: '40px', width: '220px', background: mainBg, border: `1px solid ${buttonBorder}`, borderRadius: '8px', zIndex: 50, maxHeight: '240px', overflowY: 'auto' }}>
                  {accounts.map((account) => (
                    <div key={account._id} className="flex items-center cursor-pointer transition-all" style={{ padding: '8px 12px', gap: '8px', background: selectedUser === account._id ? (isDark ? '#162456' : '#EFF6FF') : 'transparent' }} onMouseEnter={(e) => { if (selectedUser !== account._id) e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC'; }} onMouseLeave={(e) => { if (selectedUser !== account._id) e.currentTarget.style.background = 'transparent'; }} onClick={() => handleAccountSelect(account)}>
                      <img src={account.profile_picture_base64 || '/default-avatar.png'} alt={account.name} style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover' }} />
                      <span style={{ fontSize: '14px', fontWeight: 500, color: titleColor, flex: 1 }}>{account.username}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Account dropdown — mobile only */}
          <div ref={mobileDropdownRef} className="sm:hidden w-full" style={{ position: 'relative' }}>
            <button
              className="flex items-center transition-opacity hover:opacity-80 w-full"
              style={{ padding: '8px 12px', height: '36px', background: mainBg, border: `1px solid ${buttonBorder}`, borderRadius: '8px', cursor: accounts.length > 1 ? 'pointer' : 'default', gap: '4px' }}
              onClick={() => accounts.length > 1 && setIsDropdownOpen(!isDropdownOpen)}
            >
              {selectedAccount && (
                <>
                  <img src={selectedAccount.profile_picture_base64 || '/default-avatar.png'} alt={selectedAccount.name} style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }} />
                  <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, textAlign: 'left' }}>{selectedAccount.username}</span>
                </>
              )}
              {accounts.length > 1 && <ChevronDown size={20} strokeWidth={3} color={chevronColor} style={{ flexShrink: 0 }} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />}
            </button>
            {isDropdownOpen && accounts.length > 1 && (
              <div style={{ position: 'absolute', left: 0, top: '40px', width: '100%', background: mainBg, border: `1px solid ${buttonBorder}`, borderRadius: '8px', zIndex: 50, maxHeight: '240px', overflowY: 'auto' }}>
                {accounts.map((account) => (
                  <div key={account._id} className="flex items-center cursor-pointer transition-all" style={{ padding: '8px 12px', gap: '8px', background: selectedUser === account._id ? (isDark ? '#162456' : '#EFF6FF') : 'transparent' }} onMouseEnter={(e) => { if (selectedUser !== account._id) e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC'; }} onMouseLeave={(e) => { if (selectedUser !== account._id) e.currentTarget.style.background = 'transparent'; }} onClick={() => handleAccountSelect(account)}>
                    <img src={account.profile_picture_base64 || '/default-avatar.png'} alt={account.name} style={{ width: '20px', height: '20px', borderRadius: '12px', objectFit: 'cover' }} />
                    <span style={{ fontSize: '14px', fontWeight: 500, color: titleColor, flex: 1 }}>{account.username}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center" style={{ gap: '8px' }}>
            {tabs.map((tab) => (
              <button key={tab} className="flex items-center justify-center" style={{ padding: '8px 4px', background: 'transparent', border: 'none', cursor: 'pointer', position: 'relative' }} onClick={() => setActiveTab(tab)} aria-pressed={activeTab === tab}>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: activeTab === tab ? tabActiveColor : tabInactiveColor, padding: '2px 4px', textAlign: 'center' }}>
                  {tab === "Log" ? t('actions.common.log', 'Log') : t('actions.common.settings', 'Settings')}
                </span>
                {activeTab === tab && <div style={{ position: 'absolute', height: '2px', left: '8px', right: '8px', bottom: 0, background: tabSelectorColor }} />}
              </button>
            ))}
          </div>
        </div>
        <div className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'} style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '16px', gap: '24px', display: 'flex', flexDirection: 'column', background: mainBg }}>
            {activeTab === "Log" && <FollowingsLogs selectedUser={selectedUser} switchToSettings={switchToSettings} />}
            {activeTab === "Settings" && <FollowingsSettings selectedUser={selectedUser} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Followings;
